import { Content, OrderItem, Setting } from "@/types";
import { Box, Button, Divider, Drawer, IconButton, Stack, Typography } from "@mui/material"
import CardMedia from '@mui/material/CardMedia';
import Price from "@/components/Price";
import { getOrderItemPrice } from "@/utils/order";
import { sanitizeNumber } from "@/utils/numbers";
import QuantitySelector from "@/components/quantity-selector/QuantitySelector";
import { ArrowDown01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import ProductSettings from "./ProductSettings";
import { Formik, useFormikContext } from "formik";
import { FormValues } from "../ProductDetails";
import { useMemo, useState } from "react";
import { useAppSelector, useOrder } from "@/hooks";
import { v4 } from "uuid";
import { getThumbnailImage } from "@/utils/media";

interface DrawerContentProps {
    dish: Content;
    isLoading: boolean;
    onClose: () => void;
}

const DrawerContent = ({ dish, isLoading, onClose }: DrawerContentProps) => {
    const organizationId = useAppSelector(store => store?.ui?.activeOrganization);
    const activeBranch = useAppSelector((store) => store.ui.activeBranch);
    const { values, setFieldValue, handleSubmit } = useFormikContext<FormValues>();

    const increaseQuantity = () => {
        const quantity = values.quantity + 1;
        setFieldValue("quantity", quantity);
    };

    const reduceQuantity = () => {
        if (values.quantity === 1) return;
        const quantity = values.quantity - 1;
        setFieldValue("quantity", quantity);
    };
    const isDisabled = useMemo(() => {
        return Boolean(dish?.isDisabled) || Boolean(activeBranch && dish?.branchSettings?.[activeBranch]?.isDisabled);
    }, [dish?.isDisabled, dish?.branchSettings, activeBranch]);

    return (
        <Box minHeight={400}>
            <Stack direction="row" alignItems="start" spacing={3} p={4}>
                <CardMedia
                    component="img"
                    image={getThumbnailImage({ file: dish?.images, organizationId, size: '800x500' })}
                    alt={dish.title}
                    sx={{
                        width: 100,
                        height: 100,
                        borderRadius: 2,
                    }}
                />
                <Stack flex={1}>
                    <Typography variant="h2" fontWeight={500} color="textPrimary">{dish.title}</Typography>
                    <Typography variant="body1">
                        <Price currency="NIO">{dish.price != 0 ? getOrderItemPrice(dish as unknown as OrderItem) : sanitizeNumber(dish?.startingPrice)}</Price>
                    </Typography>
                </Stack>
                <IconButton onClick={onClose}>
                    <HugeiconsIcon icon={ArrowDown01Icon} />
                </IconButton>
            </Stack>

            <Divider>Configuración</Divider>
            <Box px={4} py={3}>
                <ProductSettings settings={dish.settings as Setting[]} />
            </Box>
            <Divider />
            <Stack
                direction={"row"}
                spacing={2}
                justifyContent="center"
                px={4}
                py={4}
            >
                <QuantitySelector total={values?.quantity || 1} onAdd={increaseQuantity} onRemove={reduceQuantity} minimum={1} />
                <Divider orientation="vertical" />
                <Button
                    color="primary"
                    disabled={isLoading || isDisabled}
                    onClick={(e) => {
                        e.preventDefault();
                        handleSubmit();
                    }}
                    sx={{ paddingLeft: 4, paddingRight: 4 }}
                >
                    Añadir
                    {/* <Price currency="NIO" preffix="Añadir ">
                        {multiplyByQuantity(dish?.quantity, dish.price)}
                    </Price> */}
                </Button>
            </Stack>
        </Box>
    )
}

interface Props {
    dish: Content & { settings: Setting[] } | null;
    isOpen: boolean;
    onClose: () => void;
}

const RelatedDishDrawer = ({ dish, isOpen, onClose }: Props) => {
    const { addItems } = useOrder();
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (formValues: FormValues) => {
        setIsLoading(true);

        const settings = Object.entries(formValues.settings).map(([settingId, optionIds]) => {
            const s = dish?.settings?.find(({ id }) => id === settingId);
            const newOptions = s?.options?.filter((o) => optionIds.includes(o.id));

            return {
                id: settingId,
                title: s?.title ?? "",
                type: s?.type ?? "",
                options: newOptions ?? { id: "", title: "" },
                min: s?.min ?? 0,
                max: s?.max ?? 0,
            };
        }) as Setting[];

        const payload = {
            ...dish,
            ...formValues,
            settings,
            id: v4(),
        };

        await addItems([payload as unknown as OrderItem]);

        setIsLoading(false);
        onClose();
    }

    const initialValues = useMemo(() => {
        const draft = {
            quantity: 1,
            settings: {} as Record<string, string>,
            comments: "",
        };

        if (dish?.settings) {
            dish.settings.forEach((s) => {
                if (s?.defaultOption) {
                    draft.settings[s.id] = s.defaultOption;
                }
            });
        }

        return draft;
    }, [dish?.settings]);

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
        >
            <Drawer
                anchor="bottom"
                open={isOpen}
                onClose={onClose}
            >
                {dish?.settings?.length ? (
                    <DrawerContent dish={dish} isLoading={isLoading} onClose={onClose} />
                ) : null}
            </Drawer>
        </Formik>
    )
}

export default RelatedDishDrawer;
